import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Edit2, Trash2, Briefcase, X, Check } from 'lucide-react';

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'consultant', label: 'Consultant / Visiting' },
  { value: 'internship', label: 'Internship' },
];

const emptyExp = { hospital: '', designation: '', employment_type: 'full_time', start_date: '', end_date: '', is_current: false, description: '', city: '', state: '' };

export default function Step6Experience({ data = [], onSave }) {
  const [entries, setEntries] = useState(Array.isArray(data) ? data : []);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(entries.length === 0);
  const [form, setForm] = useState(emptyExp);

  useEffect(() => { setEntries(Array.isArray(data) ? data : []); }, [data]);
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleAdd = () => {
    if (!form.hospital || !form.designation || !form.start_date) return;
    const newEntries = editing !== null ? entries.map((e, i) => i === editing ? form : e) : [...entries, form];
    setEntries(newEntries);
    setForm(emptyExp);
    setEditing(null);
    setShowForm(false);
  };
  const handleEdit = (i) => { setForm(entries[i]); setEditing(i); setShowForm(true); };
  const handleDelete = (i) => setEntries(entries.filter((_, idx) => idx !== i));
  const handleCancel = () => { setForm(emptyExp); setEditing(null); setShowForm(false); };

  return (
    <form id="step-form-6" onSubmit={(e) => { e.preventDefault(); onSave(entries); }} className="p-6 lg:p-8 space-y-5">
      {entries.length > 0 && (
        <div className="space-y-3">
          {entries.map((exp, i) => (
            <div key={i} className="p-4 rounded-xl border border-slate-200 bg-slate-50 flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#3B1C73]/10 flex items-center justify-center flex-shrink-0"><Briefcase className="w-5 h-5 text-[#3B1C73]" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-900">{exp.designation}</p>
                  {exp.is_current && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Current</span>}
                </div>
                <p className="text-sm text-slate-600">{exp.hospital}</p>
                <p className="text-xs text-slate-500 mt-1">{exp.start_date}{exp.end_date && ` → ${exp.end_date}`}{exp.is_current && ' → Present'} · {exp.employment_type?.replace(/_/g, ' ')}</p>
                {exp.description && <p className="text-xs text-slate-500 mt-1 italic">"{exp.description.substring(0, 100)}{exp.description.length > 100 ? '...' : ''}"</p>}
              </div>
              <div className="flex gap-1">
                <button type="button" onClick={() => handleEdit(i)} className="p-2 text-slate-500 hover:text-[#3B1C73] hover:bg-white rounded-lg"><Edit2 className="w-4 h-4" /></button>
                <button type="button" onClick={() => handleDelete(i)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <div className="p-5 rounded-xl border-2 border-[#3B1C73]/20 bg-purple-50/30 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-slate-900">{editing !== null ? 'Edit Experience' : 'Add Work Experience'}</h4>
            <button type="button" onClick={handleCancel} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Hospital / Organization *</Label><Input required value={form.hospital} onChange={(e) => update('hospital', e.target.value)} placeholder="e.g. AIIMS Delhi" className="mt-1.5" data-testid="exp-hospital" /></div>
            <div><Label>Designation *</Label><Input required value={form.designation} onChange={(e) => update('designation', e.target.value)} placeholder="Consultant Physician" className="mt-1.5" data-testid="exp-designation" /></div>
            <div>
              <Label>Employment Type</Label>
              <Select value={form.employment_type} onValueChange={(v) => update('employment_type', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{EMPLOYMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Delhi" className="mt-1.5" /></div>
            <div><Label>Start Date *</Label><Input type="date" required value={form.start_date} onChange={(e) => update('start_date', e.target.value)} className="mt-1.5" data-testid="exp-start" /></div>
            <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => update('end_date', e.target.value)} disabled={form.is_current} className="mt-1.5" /></div>
          </div>
          <label className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 cursor-pointer">
            <input type="checkbox" checked={form.is_current} onChange={(e) => update('is_current', e.target.checked)} className="w-5 h-5 accent-[#3B1C73]" />
            <span className="text-sm font-medium text-slate-700">Currently working here</span>
          </label>
          <div>
            <Label>Description (optional)</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="Key responsibilities, achievements..." className="mt-1.5" />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={handleCancel} className="px-4 py-2 text-slate-600 hover:bg-white rounded-lg">Cancel</button>
            <button type="button" onClick={handleAdd} className="px-4 py-2 bg-[#3B1C73] text-white rounded-lg hover:bg-[#2c1457] flex items-center gap-1 text-sm font-medium" data-testid="exp-save"><Check className="w-4 h-4" /> {editing !== null ? 'Update' : 'Add'}</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setShowForm(true)} className="w-full py-4 border-2 border-dashed border-slate-300 hover:border-[#3B1C73] hover:bg-[#3B1C73]/5 rounded-xl text-slate-600 hover:text-[#3B1C73] font-medium flex items-center justify-center gap-2" data-testid="exp-add-btn">
          <Plus className="w-5 h-5" /> Add {entries.length > 0 ? 'Another' : 'Your'} Experience
        </button>
      )}
    </form>
  );
}
